import { FormattedMessage } from 'react-intl'
import { query } from 'api/prismic'
import { ValidationProvider } from 'context/validation'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { layoutQuery } from 'prismic/fragments/layoutQuery'
import {
  singleNavigationQuery,
  topNavigationQuery,
} from 'prismic/fragments/navigationQuery'
import { formQuery } from 'prismic/queries/formQuery'
import { PrismicImage } from 'prismic/types/image'
import { PrismicRichText } from 'prismic/types/richtext'
import { Form, Layout } from 'prismic-types'

import { FormSection } from 'components/form-section/FormSection'
import { H2 } from 'components/heading/Heading'
import { PrismicPicture } from 'components/picture/PrismicPicture'
import { Section } from 'components/section/Section'
import { FormContainer } from 'containers/forms/FormContainer'
import { constructFormValidation } from 'containers/forms/Validation'
import { PrismicMeta } from 'containers/meta/PrismicMeta'
import { FormSlices } from 'containers/slices/FormSlices'

import { localeToPrismicLocale } from 'utils/i18n'
import { getStringFromQueryString } from 'utils/queryString'

type PageProps = { page: Form | null; preview?: boolean; layout: Layout | null }

export default function FormPage(
  data: InferGetServerSidePropsType<typeof getServerSideProps>
) {
  const { page } = data
  if (!page) {
    return null
  }

  return (
    <>
      <PrismicMeta data={page} layout={data.layout} />

      {!page.formid && (
        <Section container>
          <H2>
            <FormattedMessage id="contact_form_error_title" />
          </H2>

          <FormattedMessage id="contact_form_id" />
        </Section>
      )}

      {page.formid && (
        <FormSection>
          <ValidationProvider
            validationObject={constructFormValidation(page.slices ?? [])}
          >
            <FormContainer
              formId={page.formid ?? ''}
              title={(page.title ?? null) as PrismicRichText}
              content={(page.text ?? null) as PrismicRichText}
              successMessage={(page.message ?? null) as PrismicRichText}
              validation={constructFormValidation(page.slices ?? [])}
              image={
                page.image ? (
                  <PrismicPicture
                    image={page.image as PrismicImage}
                    width={1200}
                  />
                ) : undefined
              }
            >
              <FormSlices data={page.slices ?? []} />
            </FormContainer>
          </ValidationProvider>
        </FormSection>
      )}
    </>
  )
}

export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  preview = false,
  previewData = undefined,
  params,
  locale,
}) => {
  const lang = localeToPrismicLocale(locale)
  const uid = getStringFromQueryString(params?.uid) ?? ''

  // GraphQL hack for deep navigation
  const topNavData = await query(topNavigationQuery, { variables: { lang } })
  const topNav =
    topNavData?.allNavigations.edges
      ?.filter((edge) => edge && edge.node)
      .map((edge) => {
        return edge?.node._meta.uid
      }) ?? []

  const megamenuItems = async () => {
    return await Promise.all(
      topNav.map((uid) =>
        query(singleNavigationQuery, { variables: { lang, uid: uid! } })
      )
    )
  }

  const [navData, pageData, layoutData] = await Promise.all([
    megamenuItems(),
    query(formQuery, {
      previewData,
      variables: { lang, uid },
      cacheKey: `form-${uid}-${lang}`,
    }),
    query(layoutQuery, {
      variables: { lang },
      cacheKey: `layout-${lang}`,
    }),
  ])

  const layout: Layout | null = layoutData?.layout ?? null
  const page = pageData?.form ?? null
  const navigation = navData.map((navDataItem) => navDataItem?.navigation)

  if (!page) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      preview,
      layout,
      navigation,
      page,
    },
  }
}
